import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { filter, map, take, tap } from 'rxjs/operators';
import {
  Router,
  UrlTree,
  UrlSegmentGroup,
  PRIMARY_OUTLET,
  UrlSegment,
} from '@angular/router';
import { Store, createSelector } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { AppExtensionService } from '@alfresco/aca-shared';
import {
  AppStore,
  FullscreenViewerAction,
  getCurrentFolder,
  PluginPreviewAction,
  ViewerActionTypes,
  ViewNodeAction,
  ViewNodeVersionAction,
} from '@alfresco/aca-shared/store';
import { Node } from '@alfresco/js-api';
import { ViewFileAction } from '../actions/app.custom-actions';
import { getAppSelection } from '../selectors/app.custom-selectors';

export const fileToPreview = createSelector(
  getAppSelection,
  getCurrentFolder,
  (selection, folder) => {
    return {
      selection,
      folder,
    };
  }
);

@Injectable()
export class ViewerEffects {
  constructor(
    private readonly store: Store<AppStore>,
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly extensions: AppExtensionService,
    private readonly dialog: MatDialog
  ) {}

  fullscreenViewer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<FullscreenViewerAction>(ViewerActionTypes.FullScreen),
        map(() => {
          this.enterFullScreen();
        })
      ),
    { dispatch: false }
  );

  viewNode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ViewNodeAction>(ViewerActionTypes.ViewNode),
        map((action) => {
          if (action.viewNodeExtras) {
            const { location, path } = action.viewNodeExtras;

            if (location) {
              const navigation = this.getNavigationCommands(location);
              this.router.navigate([...navigation, 'preview', action.nodeId]);
            }

            if (path) {
              this.router.navigate(
                ['view', { outlets: { viewer: [action.nodeId] } }],
                {
                  queryParams: { path },
                }
              );
            }
          } else {
            this.router.navigate([
              'view',
              { outlets: { viewer: [action.nodeId] } },
            ]);
          }
        })
      ),
    { dispatch: false }
  );

  viewFile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ViewFileAction>(ViewerActionTypes.ViewFile),
        map((action) => {
          if (action.payload && action.payload.entry) {
            this.viewFileAction(action.payload.entry, action);
          } else {
            this.store
              .select(fileToPreview)
              .pipe(
                take(1),
                filter((result) => result.selection && !!result.selection.file)
              )
              .subscribe((result) => {
                this.viewFileAction(
                  result.selection.file.entry,
                  action,
                  result
                );
              });
          }
        })
      ),
    { dispatch: false }
  );

  viewNodeVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ViewNodeVersionAction>(ViewerActionTypes.ViewNodeVersion),
        map((action) => {
          this.dialog.closeAll();
          if (action.viewNodeExtras) {
            const { location, path } = action.viewNodeExtras;
            if (location) {
              const navigation = this.getNavigationCommands(location);
              this.router.navigate([...navigation, 'preview', action.nodeId], {
                queryParams: { location },
              });
            }

            if (path) {
              this.router.navigate(
                [
                  'view',
                  { outlets: { viewer: [action.nodeId, action.versionId] } },
                ],
                {
                  queryParams: { path },
                }
              );
            }
          } else {
            this.router.navigate([
              'view',
              { outlets: { viewer: [action.nodeId, action.versionId] } },
            ]);
          }
        })
      ),
    { dispatch: false }
  );

  pluginPreview$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<PluginPreviewAction>(ViewerActionTypes.PluginPreview),
        tap((action) => {
          this.router.navigate([
            action.pluginRoute,
            {
              outlets: {
                viewer: ['preview', action.nodeId],
              },
            },
          ]);
        })
      ),
    { dispatch: false }
  );

  private displayPreview(nodeId: string, parentId: string) {
    if (!nodeId) {
      return;
    }

    let previewLocation = this.router.url;
    if (previewLocation.lastIndexOf('/') > 0) {
      previewLocation = previewLocation.substring(
        0,
        this.router.url.indexOf('/', 1)
      );
    }
    previewLocation = previewLocation.replace(/\//g, '');

    const path = [previewLocation];
    if (parentId) {
      path.push(parentId);
    }
    path.push('preview', nodeId);
    this.router.navigateByUrl(path.join('/'));
  }

  enterFullScreen() {
    const container: any = document.documentElement.querySelector(
      '.adf-viewer__fullscreen-container'
    );
    if (container) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    }
  }

  private getNavigationCommands(url: string): any[] {
    const urlTree: UrlTree = this.router.parseUrl(url);
    const urlSegmentGroup: UrlSegmentGroup =
      urlTree.root.children[PRIMARY_OUTLET];

    if (!urlSegmentGroup) {
      return [url];
    }

    const urlSegments: UrlSegment[] = urlSegmentGroup.segments;

    return urlSegments.reduce(function (acc, item) {
      acc.push(item.path, item.parameters);
      return acc;
    }, []);
  }

  private viewFileAction(node: Node, action: ViewFileAction, result = null) {
    const { id, nodeId, isFile } = node as any;

    if (this.extensions.canPreviewNode(action.payload) && (isFile || nodeId)) {
      let parentId: any;
      if (!result) {
        parentId = action.parentId;
      } else {
        parentId = result.folder ? result.folder.id : null;
      }
      this.displayPreview(nodeId || id, parentId);
    }
  }
}
